/**
 * Editor Visualizer Tool
 *
 * A generic editable visualizer that renders input as an inline code editor.
 * When the user edits the content and commits, it calls onEdit() to trigger
 * pipeline re-execution from this step forward.
 *
 * This tool is ALWAYS in edit mode — its primary purpose is editing.
 * Other visualizers use the shared InlineEditor only when toggled to edit mode.
 */

import React from 'react';

import { InlineEditor } from '@/components/visualizers/InlineEditor';
import type { ISemanticType } from '@/interfaces/ISemanticType';
import type { IVisualizerTool, VisualizerRenderContext } from '@/interfaces/ITool';

class EditorVisualizerToolImpl implements IVisualizerTool {
  readonly id = 'editor-visualizer';
  readonly name = 'Editor';
  readonly description = 'Inline code editor — view and edit data mid-pipeline, then re-run downstream steps';
  readonly version = '1.0.0';
  readonly type = 'visualizer' as const;
  readonly category = 'Visualizers';
  readonly editable = true;
  readonly keywords = ['editor', 'edit', 'text', 'code', 'visualizer', 'editable', 'modify', 'write', 'input', 'textarea'];

  readonly supportedInputTypes: ISemanticType[] = [];

  readonly outputType = undefined;

  supportsInput(): boolean {
    // Accepts any text input
    return true;
  }

  readonly attributes = [
    {
      name: 'wordWrap',
      type: 'boolean' as const,
      label: 'Word Wrap',
      defaultValue: true,
      description: 'Wrap long lines instead of horizontal scrolling',
    },
    {
      name: 'fontSize',
      type: 'number' as const,
      label: 'Font Size',
      defaultValue: 13,
      description: 'Editor font size in pixels',
      validation: { min: 8, max: 24 },
    },
  ];

  render(context: VisualizerRenderContext): React.ReactElement {
    const { input, config, onEdit } = context;

    return (
      <InlineEditor
        input={input}
        onEdit={onEdit}
        wordWrap={(config.wordWrap as boolean) ?? true}
        fontSize={(config.fontSize as number) ?? 13}
      />
    );
  }

  toText(input: string): string {
    return input;
  }
}

export const EditorVisualizerTool = new EditorVisualizerToolImpl();
